import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
  type BreadcrumbLayoutResult,
  type DisplayBreadcrumbItem,
  pickBreadcrumbLayout,
} from '../lib/breadcrumbDisplay'
import type { BreadcrumbItem } from '../types/breadcrumb'

/**
 * 根据容器宽度选择面包屑布局：有空间则完整展示；不足时藏首页/折中间/保留当前模块，最后才对段文案做末尾省略。
 */
export function useBreadcrumbDisplayLayout(
  items: BreadcrumbItem[],
  dense: boolean,
): {
  navRef: React.RefObject<HTMLElement>
  rowRef: React.RefObject<HTMLDivElement>
  displayItems: DisplayBreadcrumbItem[]
  ellipsizeMiddle: boolean
  ellipsizeLast: boolean
} {
  const navRef = useRef<HTMLElement>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const [layout, setLayout] = useState<BreadcrumbLayoutResult>(() => ({
    items: items.map((item) => ({
      ...item,
      displayLabel: item.label.trim(),
      title: item.title ?? item.label,
    })),
    ellipsizeMiddle: false,
    ellipsizeLast: false,
  }))

  const itemsSignature = useMemo(
    () => items.map((i) => `${i.label}\u0001${i.to ?? ''}`).join('\u0002'),
    [items],
  )

  const recompute = useCallback(() => {
    const nav = navRef.current
    if (!nav) return
    const next = pickBreadcrumbLayout(items, nav.clientWidth, dense)
    setLayout(next)
  }, [items, dense])

  useLayoutEffect(() => {
    recompute()
  }, [recompute, itemsSignature])

  useEffect(() => {
    const nav = navRef.current
    if (!nav || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      recompute()
    })
    observer.observe(nav)
    return () => observer.disconnect()
  }, [recompute])

  return {
    navRef,
    rowRef,
    displayItems: layout.items,
    ellipsizeMiddle: layout.ellipsizeMiddle,
    ellipsizeLast: layout.ellipsizeLast,
  }
}
